import React from 'react';
import { motion } from 'framer-motion';
import { Navigation, Clock, Waves, Anchor, Compass, Fish } from 'lucide-react';

interface Destino {
    icon: React.ReactNode;
    name: string;
    description: string;
    distance: string;
    time: string;
    difficulty: 'Fácil' | 'Moderado' | 'Avançado';
}

const destinos: Destino[] = [
    {
        icon: <Waves className="w-6 h-6" />,
        name: 'Praia do Capri',
        description: 'A praia mais badalada de São Chico, com águas calmas, quiosques e acesso fácil por mar. Perfeita para famílias.',
        distance: '2 km',
        time: '~10 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Anchor className="w-6 h-6" />,
        name: 'Ilha da Paz',
        description: 'Refúgio paradisíaco dentro da Baía da Babitonga. Ideal para ancorar, mergulhar e curtir um piquenique no mar.',
        distance: '5 km',
        time: '~20 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Fish className="w-6 h-6" />,
        name: 'Parcel do Capri',
        description: 'Ponto de pesca lendário entre os moradores locais. Robalos, badejos e tainhas garantidas para o pescador paciente.',
        distance: '3 km',
        time: '~15 min',
        difficulty: 'Moderado',
    },
    {
        icon: <Compass className="w-6 h-6" />,
        name: 'Forte Marechal Luz',
        description: 'Patrimônio histórico no alto da ilha. Acesso por mar com vista panorâmica da baía e do litoral norte catarinense.',
        distance: '8 km',
        time: '~30 min',
        difficulty: 'Moderado',
    },
    {
        icon: <Navigation className="w-6 h-6" />,
        name: 'Praia da Enseada',
        description: 'Extensa faixa de areia com águas cristalinas. Um dos destinos favoritos de quem navega pelo entorno de São Chico.',
        distance: '6 km',
        time: '~25 min',
        difficulty: 'Fácil',
    },
    {
        icon: <Waves className="w-6 h-6" />,
        name: 'Canal do Linguado',
        description: 'Passagem histórica que conecta a Baía da Babitonga ao mar aberto. Navegação técnica com cenário deslumbrante.',
        distance: '12 km',
        time: '~40 min',
        difficulty: 'Avançado',
    },
];

const difficultyColors: Record<string, string> = {
    'Fácil': 'bg-green-100 text-green-700',
    'Moderado': 'bg-amber-100 text-amber-700',
    'Avançado': 'bg-sky-100 text-sky-700',
};

const DestinosSFSSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-background relative overflow-hidden">
        <div
            className="absolute right-0 top-1/3 w-[400px] h-[400px] pointer-events-none"
            style={{
                background: 'radial-gradient(circle, hsl(43 74% 53% / 0.05), transparent 65%)',
                filter: 'blur(40px)',
            }}
        />

        <div className="container mx-auto px-6 relative z-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Destinos
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                    Destinos da Baía da Babitonga
                </h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                    Da Praia do Capri ao Canal do Linguado — a maior baía navegável de SC oferece rotas inesquecíveis partindo de São Francisco do Sul.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {destinos.map((dest, i) => (
                    <motion.div
                        key={dest.name}
                        initial={{ opacity: 0, y: 25 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        whileHover={{ scale: 1.03, y: -5 }}
                        className="group bg-white border border-border rounded-xl p-6 cursor-default transition-all duration-300 hover:shadow-xl hover:border-gold/30 relative overflow-hidden"
                    >
                        <div
                            className="absolute -top-10 -right-10 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                                background: 'radial-gradient(circle, hsl(43 74% 53% / 0.15), transparent 70%)',
                            }}
                        />

                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/5 group-hover:bg-gold/10 flex items-center justify-center text-primary group-hover:text-gold transition-colors duration-300">
                                    {dest.icon}
                                </div>
                                <h4 className="font-bold text-lg text-primary">{dest.name}</h4>
                            </div>
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${difficultyColors[dest.difficulty]}`}>
                                {dest.difficulty}
                            </span>
                        </div>

                        <p className="text-muted-foreground text-sm leading-relaxed mb-5">{dest.description}</p>

                        <div className="flex items-center gap-6 pt-4 border-t border-border/60">
                            <div className="flex items-center gap-1.5 text-sm">
                                <Navigation className="w-3.5 h-3.5 text-gold" />
                                <span className="font-semibold text-primary">{dest.distance}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm">
                                <Clock className="w-3.5 h-3.5 text-gold" />
                                <span className="font-semibold text-primary">{dest.time}</span>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

export default DestinosSFSSection;
